import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Trash2, Check, X, Plus, Tag as TagIcon, ArrowLeft, Loader2 } from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { SectionCard, Chip } from '../design/primitives';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { name: '', color: '#1E5FA8', quantityType: '', unitPrice: 0 };

export default function TagManagement() {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [, setInlineEdit] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setTags(response.data);
    } catch {
      toast.error('Etiketler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTags(); }, []);

  const handleInlineUpdate = async (tagId, field, value) => {
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return;
    try {
      await axios.patch(`${API}/tags/${tagId}`, {
        name: tag.name,
        color: tag.color,
        quantityType: field === 'quantityType' ? value : (tag.quantityType || ''),
        unitPrice: field === 'unitPrice' ? parseFloat(value) || 0 : (tag.unitPrice ?? 0),
      });
      toast.success('Güncellendi');
      fetchTags();
    } catch {
      toast.error('Güncellenemedi');
    }
    setInlineEdit(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Etiket adı gerekli');
      return;
    }
    try {
      await axios.post(`${API}/tags`, { ...form, unitPrice: parseFloat(form.unitPrice) || 0 });
      toast.success('Etiket oluşturuldu');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Etiket oluşturulamadı');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Etiket adı gerekli');
      return;
    }
    try {
      await axios.patch(`${API}/tags/${editingTag.id}`, { ...form, unitPrice: parseFloat(form.unitPrice) || 0 });
      toast.success('Etiket güncellendi');
      setEditingTag(null);
      setForm(EMPTY_FORM);
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Etiket güncellenemedi');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/tags/${confirmDelete.id}`);
      toast.success('Etiket silindi');
      setConfirmDelete(null);
      fetchTags();
    } catch (error) {
      let msg = 'Etiket silinemedi';
      if (error.response?.data?.detail) {
        msg = error.response.data.detail;
        if (msg.includes('kullanılıyor') || msg.includes('Kullanımda') || msg.includes('görevde')) {
          msg = 'Kullanımda olan etiketler silinemez';
        }
      }
      toast.error(msg);
    }
  };

  const startEdit = (tag) => {
    setEditingTag(tag);
    setForm({
      name: tag.name,
      color: tag.color,
      quantityType: tag.quantityType || '',
      unitPrice: tag.unitPrice ?? 0,
    });
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setForm(EMPTY_FORM);
  };

  const headerActions = useMemo(() => (
    <>
      <button className="ox-btn ox-btn-ghost" onClick={() => navigate('/tasks')}>
        <ArrowLeft size={13} style={{ marginRight: 6 }} />
        Görevler
      </button>
      <button className="ox-btn ox-btn-primary" onClick={() => { setForm(EMPTY_FORM); setShowAdd(true); }}>
        <Plus size={13} style={{ marginRight: 6 }} />
        Yeni Etiket
      </button>
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);

  usePageHeader([{ label: 'Etiket Yönetimi' }], headerActions, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <SectionCard title={`Etiketler (${tags.length})`} subtitle="Görevler için etiketler oluşturun ve yönetin" pad={false}>
        {tags.length === 0 ? (
          <div className="py-12 text-center">
            <TagIcon size={32} className="mx-auto mb-3 opacity-30" />
            <div className="text-sm font-medium">Henüz etiket yok</div>
            <div className="text-xs ox-text-muted mt-1">Yeni etiket ekleyerek başlayın</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider ox-text-dim" style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left px-3 py-2 font-medium">Renk</th>
                  <th className="text-left px-3 py-2 font-medium">Etiket</th>
                  <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Durum</th>
                  <th className="text-left px-3 py-2 font-medium">Miktar Türü</th>
                  <th className="text-right px-3 py-2 font-medium">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {tags.map(tag => (
                  <tr key={tag.id} className="ox-row-hover" style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2">
                      <div
                        className="rounded-full"
                        style={{
                          width: 22,
                          height: 22,
                          background: tag.color,
                          border: '1px solid var(--border)',
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[13px] font-medium">{tag.name}</div>
                      {tag.isDefault && (
                        <div className="sm:hidden mt-1">
                          <Chip color="var(--accent)" bg="var(--accent-weak)">Varsayılan</Chip>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      {tag.isDefault && (
                        <Chip color="var(--accent)" bg="var(--accent-weak)">Varsayılan</Chip>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="ox-select"
                        value={tag.quantityType || ''}
                        onChange={(e) => handleInlineUpdate(tag.id, 'quantityType', e.target.value)}
                      >
                        <option value="">Seçiniz</option>
                        <option value="adet">Adet</option>
                        <option value="sure">Süre</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button className="ox-btn ox-btn-ghost" onClick={() => startEdit(tag)} title="Düzenle">
                          <Pencil size={13} />
                        </button>
                        {!tag.isDefault && (
                          <button
                            className="ox-btn ox-btn-ghost"
                            onClick={() => setConfirmDelete(tag)}
                            title="Sil"
                            style={{ color: 'var(--danger)' }}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {(showAdd || editingTag) && (
        <div className="ox-modal-backdrop" onClick={() => { setShowAdd(false); cancelEdit(); }}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-sm font-semibold flex items-center gap-2">
                {editingTag ? <Pencil size={14} /> : <Plus size={14} />}
                {editingTag ? 'Etiketi Düzenle' : 'Yeni Etiket Ekle'}
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => { setShowAdd(false); cancelEdit(); }}>
                <X size={14} />
              </button>
            </div>
            <form onSubmit={editingTag ? handleEdit : handleAdd}>
              <div className="ox-modal-body space-y-4">
                <div className="ox-field">
                  <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                    Etiket Adı {!editingTag?.isDefault && <span style={{ color: 'var(--danger)' }}>*</span>}
                  </label>
                  <input
                    type="text"
                    className="ox-input w-full mt-1"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Örn: Animasyon, Tasarım, Yazılım"
                    required
                    disabled={editingTag?.isDefault}
                  />
                  {editingTag?.isDefault && (
                    <div className="text-[11px] ox-text-muted mt-1" style={{ color: 'var(--warn)' }}>
                      Varsayılan etiketin adı değiştirilemez
                    </div>
                  )}
                </div>

                <div className="ox-field">
                  <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">Renk</label>
                  <div className="flex items-center gap-3 mt-1 ox-surface-muted rounded-md p-3">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="rounded-md cursor-pointer"
                      style={{ width: 44, height: 44, border: '1px solid var(--border)' }}
                    />
                    <div className="flex-1">
                      <div className="text-[11px] ox-text-muted">Seçili renk</div>
                      <div className="ox-mono text-sm font-semibold">{form.color}</div>
                    </div>
                    <div
                      className="rounded-md"
                      style={{
                        width: 44,
                        height: 44,
                        background: form.color,
                        border: '1px solid var(--border)',
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="ox-field">
                    <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">Miktar Türü</label>
                    <select
                      className="ox-select w-full mt-1"
                      value={form.quantityType}
                      onChange={(e) => setForm({ ...form, quantityType: e.target.value })}
                    >
                      <option value="">Seçiniz</option>
                      <option value="adet">Adet</option>
                      <option value="sure">Süre</option>
                    </select>
                  </div>
                  <div className="ox-field">
                    <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">Birim Fiyatı (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="ox-input w-full mt-1"
                      value={form.unitPrice}
                      onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn" onClick={() => { setShowAdd(false); cancelEdit(); }}>
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  {editingTag ? <Check size={13} style={{ marginRight: 6 }} /> : <Plus size={13} style={{ marginRight: 6 }} />}
                  {editingTag ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="ox-modal-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-sm font-semibold">Etiketi sil</div>
              <button className="ox-btn ox-btn-ghost" onClick={() => setConfirmDelete(null)}>
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <div className="text-sm ox-text-muted">
                <span className="font-medium" style={{ color: 'var(--text)' }}>{confirmDelete.name}</span> etiketini silmek istediğinizden emin misiniz?
              </div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn" onClick={() => setConfirmDelete(null)}>İptal</button>
              <button className="ox-btn ox-btn-danger" onClick={handleDelete}>
                <Trash2 size={13} style={{ marginRight: 6 }} />
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
